import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";
import { Link, useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import {
  FiArrowLeft,
  FiChevronRight,
  FiLayers,
  FiSettings,
  FiList,
  FiRefreshCw,
  FiSave,
  FiSearch,
  FiCopy,
  FiCheck,
  FiInfo,
  FiEye,
  FiCalendar,
  FiClock,
  FiAward,
  FiZap,
} from "react-icons/fi";
import {
  getSlotsSettings,
  updateSlotsSettings,
  getSlotsHistory,
} from "../api/tapGameApi";
import { getEvents } from "../api/eventApi";
import { formatUtcDateTimeWithSeconds } from "../utils/dateUtils";
import GameAnalyticsCharts from "./components/GameAnalyticsCharts";
import GameSessionDetailModal from "./components/GameSessionDetailModal";
import dayjs from "dayjs";

const formatDateTime = (dateStr) => {
  if (!dateStr) return "—";
  try {
    return formatUtcDateTimeWithSeconds(dateStr);
  } catch {
    return String(dateStr);
  }
};

const computeSlotsStats = (list = [], serverStats = null, currentDailyStats = [], prevStats = null) => {
  const todayStr = dayjs().format("YYYY-MM-DD");

  // 1. Check server-provided stats
  const serverTodaySessions = serverStats?.todaySessions;
  const serverTodayPoints = serverStats?.todayPoints;
  const serverEventPoints = Number(
    serverStats?.eventTotalPoints ?? serverStats?.truePointsAwarded ?? serverStats?.totalPointsAwarded ?? serverStats?.totalSlotsPointsAwarded ?? 0
  );
  const serverEventSessions = Number(
    serverStats?.eventTotalSessions ?? serverStats?.totalSessions ?? serverStats?.totalSlotsSessions ?? 0
  );

  // 2. Client fallback for today's stats if server did not calculate them
  let todaySessionsCalc = 0;
  let todayPointsCalc = 0;

  const sourceDaily = Array.isArray(currentDailyStats) && currentDailyStats.length > 0 ? currentDailyStats : [];
  const todayDailyRow = sourceDaily.find(
    (d) => d?.date === todayStr || dayjs(d?.date).format("YYYY-MM-DD") === todayStr
  );

  if (todayDailyRow) {
    todaySessionsCalc = Number(todayDailyRow.sessions || todayDailyRow.count || 0);
    todayPointsCalc = Number(todayDailyRow.points || todayDailyRow.claimedPoints || 0);
  } else if (Array.isArray(list) && list.length > 0) {
    const todayList = list.filter((row) => {
      const d = row.createdAt || row.startedAt || row.completedAt;
      return d && dayjs(d).format("YYYY-MM-DD") === todayStr;
    });
    todaySessionsCalc = todayList.length;
    todayPointsCalc = todayList.reduce((sum, r) => {
      if (r.status === "completed" || r.status === "Claimed") {
        return sum + Number(r.claimedPoints ?? r.pointsAwarded ?? 0);
      }
      return sum;
    }, 0);
  }

  // Fallback for event totals from list if server gave 0
  const completedList = list.filter((h) => h.status === "completed" || h.status === "Claimed");
  const listEventPoints = completedList.reduce(
    (acc, h) => acc + Number(h.claimedPoints ?? h.pointsAwarded ?? 0),
    0
  );

  return {
    todaySessions: serverTodaySessions != null ? Number(serverTodaySessions) : todaySessionsCalc,
    todayPoints: serverTodayPoints != null ? Number(serverTodayPoints) : todayPointsCalc,
    eventTotalPoints: serverEventPoints > 0 ? serverEventPoints : (listEventPoints > 0 ? listEventPoints : (prevStats?.eventTotalPoints || 0)),
    eventTotalSessions: serverEventSessions > 0 ? serverEventSessions : (list.length > 0 ? list.length : (prevStats?.eventTotalSessions || 0)),
    totalSessions: serverEventSessions > 0 ? serverEventSessions : (list.length > 0 ? list.length : (prevStats?.totalSessions || 0)),
    completedSessions: Number(serverStats?.completedSessions ?? completedList.length),
    totalPointsAwarded: serverEventPoints > 0 ? serverEventPoints : (listEventPoints > 0 ? listEventPoints : (prevStats?.totalPointsAwarded || 0)),
  };
};

export default function SlotsGame() {
  const navigate = useNavigate();

  // Navigation back handler
  const handleGoBack = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate("/games");
    }
  };

  // Active Tab: "settings" or "history"
  const [activeTab, setActiveTab] = useState("settings");

  // Settings State
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [formSettings, setFormSettings] = useState({
    name: "777 Slots",
    description: "Spin the reels, hit 777 Jackpot and earn massive event points!",
    jackpotPoints: 500,
    tripleBarPoints: 200,
    tripleCherryPoints: 100,
    otherTriplePoints: 50,
    match2Points: 20,
    totalSpins: 5,
    cooldownSeconds: 180,
    isActive: true,
  });

  // Spin Outcome Probability Distribution State
  const [pointProbabilities, setPointProbabilities] = useState({
    jackpotPercentage: 2,
    tripleBarPercentage: 5,
    tripleCherryPercentage: 10,
    otherTriplePercentage: 15,
    match2Percentage: 35,
    lossPercentage: 33,
  });

  // Event State: Default to active event
  const [eventsList, setEventsList] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState("");

  // 4 Core Metrics Requested:
  // 1. todaySessions
  // 2. todayPoints
  // 3. eventTotalPoints
  // 4. eventTotalSessions
  const [stats, setStats] = useState({
    todaySessions: 0,
    todayPoints: 0,
    eventTotalPoints: 0,
    eventTotalSessions: 0,
    totalSessions: 0,
    completedSessions: 0,
    totalPointsAwarded: 0,
  });

  // Daily Analytics Data for Visual Charts
  const [dailyStats, setDailyStats] = useState([]);
  const [analyticsHistory, setAnalyticsHistory] = useState([]);

  // History State
  const [historyLoading, setHistoryLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const perPage = 20;
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 1,
    page: 1,
    perPage: 20,
  });

  // UI state
  const [copiedSessionId, setCopiedSessionId] = useState("");
  const [selectedSessionModal, setSelectedSessionModal] = useState(null);
  const searchDebounceTimer = useRef(null);


  // Fetch Settings
  const fetchSettings = useCallback(async (showToast = false) => {
    setSettingsLoading(true);
    try {
      const res = await getSlotsSettings();
      if (res?.status && res?.data) {
        const d = res.data;
        setFormSettings({
          name: d.name || "777 Slots",
          description: d.description || "Spin the reels, hit 777 Jackpot and earn massive event points!",
          jackpotPoints: Number(d.jackpotPoints ?? 500),
          tripleBarPoints: Number(d.tripleBarPoints ?? 200),
          tripleCherryPoints: Number(d.tripleCherryPoints ?? 100),
          otherTriplePoints: Number(d.otherTriplePoints ?? 50),
          match2Points: Number(d.match2Points ?? 20),
          totalSpins: Number(d.totalSpins ?? 5),
          cooldownSeconds: Number(d.cooldownSeconds ?? 180),
          isActive: Boolean(d.isActive === 1 || d.isActive === true || d.isActive === "1"),
        });

        if (d.pointProbabilities && typeof d.pointProbabilities === "object") {
          setPointProbabilities({
            jackpotPercentage: Number(d.pointProbabilities.jackpotPercentage ?? 2),
            tripleBarPercentage: Number(d.pointProbabilities.tripleBarPercentage ?? 5),
            tripleCherryPercentage: Number(d.pointProbabilities.tripleCherryPercentage ?? 10),
            otherTriplePercentage: Number(d.pointProbabilities.otherTriplePercentage ?? 15),
            match2Percentage: Number(d.pointProbabilities.match2Percentage ?? 35),
            lossPercentage: Number(d.pointProbabilities.lossPercentage ?? 33),
          });
        }

        if (Array.isArray(d.dailyStats) && d.dailyStats.length > 0) {
          setDailyStats(d.dailyStats);
        }

        if (d.stats && (!selectedEventId || selectedEventId === "all")) {
          setStats((prev) => computeSlotsStats([], d.stats, d.dailyStats || [], prev));
        }

        if (showToast) toast.success("777 Slots settings reloaded!");
      }
    } catch (err) {
      console.warn("Failed to load 777 Slots settings, using defaults:", err.message);
      if (showToast) {
        toast.error(err.message || "Failed to load 777 Slots settings");
      }
    } finally {
      setSettingsLoading(false);
    }
  }, [selectedEventId]);

  // Fetch History with Event filter
  const fetchHistory = useCallback(async (showToast = false, overrideEventId = null) => {
    setHistoryLoading(true);
    const activeEventFilter = overrideEventId !== null ? overrideEventId : selectedEventId;
    try {
      const res = await getSlotsHistory({
        page,
        perPage,
        search: debouncedSearch,
        status: statusFilter,
        eventId: activeEventFilter && activeEventFilter !== "all" ? activeEventFilter : undefined,
      });

      if (res?.status) {
        const rawHistoryList = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.history)
            ? res.data.history
            : [];

        setHistory(rawHistoryList);

        const serverPagination = res.pagination || res.data?.pagination || {};
        const totalRecords =
          typeof serverPagination.total === "number"
            ? serverPagination.total
            : typeof res.total === "number"
              ? res.total
              : rawHistoryList.length;

        const serverDaily = res.dailyStats || res.data?.dailyStats || [];
        if (Array.isArray(serverDaily) && serverDaily.length > 0) {
          setDailyStats(serverDaily);
        }

        const serverStats = res.stats || res.data?.stats;
        setStats((prev) => computeSlotsStats(rawHistoryList, serverStats, serverDaily, prev));

        const totalPagesCount =
          typeof serverPagination.lastPage === "number"
            ? serverPagination.lastPage
            : typeof serverPagination.totalPages === "number"
              ? serverPagination.totalPages
              : Math.max(1, Math.ceil(totalRecords / perPage));

        const currentPageNum =
          typeof serverPagination.currentPage === "number"
            ? serverPagination.currentPage
            : typeof serverPagination.page === "number"
              ? serverPagination.page
              : page;

        setPagination({
          total: totalRecords,
          totalPages: totalPagesCount,
          page: currentPageNum,
          perPage,
        });

        if (showToast) toast.success("777 Slots history reloaded!");
      }
    } catch (err) {
      console.warn("Failed to load 777 Slots history:", err.message);
      if (showToast) {
        toast.error(err.message || "Failed to load history");
      }
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [page, perPage, debouncedSearch, statusFilter, selectedEventId]);

  // Fetch Events Catalog and default to active/running event
  useEffect(() => {
    async function initEventsAndData() {
      let defaultId = "all";
      try {
        const res = await getEvents({ limit: 100, page: 1, status: "all" });
        const data = res?.data || res;
        const list = data?.events || (Array.isArray(data) ? data : []);
        setEventsList(list);

        // Find active/running event
        const runningEvent = list.find((e) => e.status?.toLowerCase() === "running");
        if (runningEvent) {
          defaultId = String(runningEvent.eventId || runningEvent.id);
        }
      } catch (err) {
        console.warn("Failed to load events list:", err);
      }

      setSelectedEventId(defaultId);
      fetchSettings();
      fetchHistory(false, defaultId);

      // Wider history sample for charts
      getSlotsHistory({ page: 1, perPage: 100, eventId: defaultId !== "all" ? defaultId : undefined })
        .then((res) => {
          if (res?.status) {
            const list = Array.isArray(res.data)
              ? res.data
              : Array.isArray(res.data?.history)
                ? res.data.history
                : [];
            if (list.length > 0) setAnalyticsHistory(list);
            if (Array.isArray(res.dailyStats) && res.dailyStats.length > 0) {
              setDailyStats(res.dailyStats);
            }
          }
        })
        .catch(() => {});
    }

    initEventsAndData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch History when activeTab becomes history or when filters/pagination change
  useEffect(() => {
    if (activeTab === "history") {
      fetchHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, page, perPage, debouncedSearch, statusFilter]);

  // Event Selection Handler (1. Event Change Button)
  const handleEventChange = (newEventId) => {
    setSelectedEventId(newEventId);
    setPage(1);
    fetchHistory(false, newEventId);
    // Refresh chart history for this event
    getSlotsHistory({ page: 1, perPage: 100, eventId: newEventId !== "all" ? newEventId : undefined })
      .then((res) => {
        if (res?.status) {
          const list = Array.isArray(res.data)
            ? res.data
            : Array.isArray(res.data?.history)
              ? res.data.history
              : [];
          setAnalyticsHistory(list);
          if (Array.isArray(res.dailyStats) && res.dailyStats.length > 0) {
            setDailyStats(res.dailyStats);
          }
        }
      })
      .catch(() => {});
  };

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    clearTimeout(searchDebounceTimer.current);
    searchDebounceTimer.current = setTimeout(() => {
      setDebouncedSearch(val);
      setPage(1);
    }, 350);
  };

  const handleCopySession = (sessionId) => {
    if (!sessionId) return;
    navigator.clipboard.writeText(sessionId);
    setCopiedSessionId(sessionId);
    toast.success("Session ID copied!");
    setTimeout(() => setCopiedSessionId(""), 2000);
  };

  // Probability helper methods
  const handleSlotsProbChange = (field, value) => {
    setPointProbabilities((prev) => ({
      ...prev,
      [field]: Math.max(0, Math.min(100, Number(value) || 0)),
    }));
  };

  const handleSlotsResetProbabilities = () => {
    setPointProbabilities({
      jackpotPercentage: 2,
      tripleBarPercentage: 5,
      tripleCherryPercentage: 10,
      otherTriplePercentage: 15,
      match2Percentage: 35,
      lossPercentage: 33,
    });
    toast.success("Reset to default 777 Slots probabilities!");
  };

  const handleSlotsAutoBalance = () => {
    const fixedSum =
      Number(pointProbabilities.jackpotPercentage || 0) +
      Number(pointProbabilities.tripleBarPercentage || 0) +
      Number(pointProbabilities.tripleCherryPercentage || 0) +
      Number(pointProbabilities.otherTriplePercentage || 0) +
      Number(pointProbabilities.match2Percentage || 0);
    const remainingForLoss = Math.max(0, 100 - fixedSum);
    setPointProbabilities((prev) => ({
      ...prev,
      lossPercentage: remainingForLoss,
    }));
    toast.success(`Loss percentage balanced to ${remainingForLoss}% to total 100%!`);
  };

  const handleSaveSettings = async (e) => {
    if (e) e.preventDefault();

    const jackpot = Number(formSettings.jackpotPoints);
    const tripleBar = Number(formSettings.tripleBarPoints);
    const tripleCherry = Number(formSettings.tripleCherryPoints);
    const otherTriple = Number(formSettings.otherTriplePoints);
    const match2 = Number(formSettings.match2Points);
    const spins = Number(formSettings.totalSpins);
    const cDown = Number(formSettings.cooldownSeconds);

    if (isNaN(jackpot) || jackpot <= 0) {
      toast.error("Jackpot points must be greater than 0.");
      return;
    }
    if (isNaN(tripleBar) || tripleBar <= 0) {
      toast.error("Triple bar points must be greater than 0.");
      return;
    }
    if (isNaN(tripleCherry) || tripleCherry <= 0) {
      toast.error("Triple cherry points must be greater than 0.");
      return;
    }
    if (isNaN(otherTriple) || otherTriple <= 0) {
      toast.error("Other triple match points must be greater than 0.");
      return;
    }
    if (isNaN(match2) || match2 <= 0) {
      toast.error("Match 2 points must be greater than 0.");
      return;
    }
    if (isNaN(spins) || spins < 1 || spins > 20) {
      toast.error("Spins per session must be between 1 and 20.");
      return;
    }
    if (isNaN(cDown) || cDown < 0) {
      toast.error("Cooldown time cannot be negative.");
      return;
    }

    const probSum =
      Number(pointProbabilities.jackpotPercentage || 0) +
      Number(pointProbabilities.tripleBarPercentage || 0) +
      Number(pointProbabilities.tripleCherryPercentage || 0) +
      Number(pointProbabilities.otherTriplePercentage || 0) +
      Number(pointProbabilities.match2Percentage || 0) +
      Number(pointProbabilities.lossPercentage || 0);

    if (Math.abs(probSum - 100) > 0.5) {
      toast.error(
        `Spin probabilities sum is ${probSum.toFixed(1)}%. Please balance it to 100% using Auto Balance.`
      );
      return;
    }

    setSavingSettings(true);
    try {
      const payload = {
        jackpotPoints: jackpot,
        tripleBarPoints: tripleBar,
        tripleCherryPoints: tripleCherry,
        otherTriplePoints: otherTriple,
        match2Points: match2,
        totalSpins: spins,
        cooldownSeconds: cDown,
        isActive: formSettings.isActive ? 1 : 0,
        pointProbabilities,
      };

      const res = await updateSlotsSettings(payload);
      if (res?.status) {
        toast.success(res.message || "777 Slots settings saved successfully!");
        fetchSettings();
      } else {
        throw new Error(res?.message || "Failed to save settings");
      }
    } catch (err) {
      toast.error(err.message || "Failed to save settings");
    } finally {
      setSavingSettings(false);
    }
  };

  // Resolve current active event label
  const activeRunningEvent = useMemo(() => {
    return eventsList.find((e) => e.status?.toLowerCase() === "running");
  }, [eventsList]);

  const selectedEventObj = useMemo(() => {
    if (!selectedEventId || selectedEventId === "all") return null;
    return eventsList.find(
      (e) => String(e.eventId) === String(selectedEventId) || String(e.id) === String(selectedEventId)
    );
  }, [selectedEventId, eventsList]);

  const selectedEventName = selectedEventObj
    ? selectedEventObj.eventName || `Event #${selectedEventObj.id}`
    : selectedEventId === "all"
    ? "All Events"
    : "Active Event";

  return (
    <div className="flex bg-gray-100 min-h-screen">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />
        <Toaster position="top-right" />

        <div className="p-5 sm:p-7 space-y-5 w-full">
          {/* Breadcrumb and Back Button - Sleek single inline row matching Screenshot */}
          <div className="flex items-center gap-2 text-xs md:text-sm text-gray-500">
            <button
              onClick={handleGoBack}
              className="flex items-center gap-1 hover:text-black transition cursor-pointer p-1 -ml-1 rounded hover:bg-gray-200"
              title="Go back"
            >
              <FiArrowLeft className="h-4 w-4" />
              <span>Back</span>
            </button>
            <FiChevronRight className="h-3 w-3 text-gray-400" />
            <Link to="/dashboard" className="hover:text-black transition">
              Dashboard
            </Link>
            <FiChevronRight className="h-3 w-3 text-gray-400" />
            <Link to="/games" className="hover:text-black transition">
              Games Management
            </Link>
            <FiChevronRight className="h-3 w-3 text-gray-400" />
            <span className="text-red-600 font-bold">777 Slots</span>
          </div>

          {/* Header Row */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-red-100 text-red-700 flex items-center justify-center text-2xl shadow-2xs">
                🎰
              </div>
              <div>
                <div className="flex items-center gap-2.5">
                  <h1 className="text-xl sm:text-2xl font-black text-gray-900 tracking-tight">
                    777 Slot Machine
                  </h1>
                  <span
                    className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full ${
                      formSettings.isActive
                        ? "bg-emerald-50 text-emerald-700 border border-emerald-300"
                        : "bg-gray-100 text-gray-600 border border-gray-300"
                    }`}
                  >
                    {formSettings.isActive ? "LIVE" : "DISABLED"}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-0.5">
                  Configure server-driven jackpot rewards, 3-reel point combinations, and inspect spin audit history.
                </p>
              </div>
            </div>

            {/* Top Game Switcher & Refresh Button */}
            <div className="flex items-center gap-2.5 flex-wrap">
              <div className="flex items-center bg-gray-200/80 p-1 rounded-xl text-xs font-bold">
                <Link
                  to="/games"
                  className="px-2.5 py-1.5 rounded-lg text-gray-600 hover:text-black hover:bg-white/60 transition flex items-center gap-1"
                  title="All Games Priority & Overview"
                >
                  <FiLayers className="h-3.5 w-3.5" /> All Games
                </Link>
                <Link
                  to="/boom-cards"
                  className="px-3 py-1.5 rounded-lg text-gray-600 hover:text-black hover:bg-white/60 transition flex items-center gap-1.5"
                >
                  <span>🃏</span> Boom Cards
                </Link>
                <Link
                  to="/tap-game"
                  className="px-3 py-1.5 rounded-lg text-gray-600 hover:text-black hover:bg-white/60 transition flex items-center gap-1.5"
                >
                  <span>🎮</span> Tap Game
                </Link>
                <button
                  type="button"
                  className="px-3 py-1.5 rounded-lg bg-white text-black shadow-xs flex items-center gap-1.5 cursor-default"
                >
                  <span>🎰</span> 777 Slots
                </button>
                <Link
                  to="/spin-wheel"
                  className="px-3 py-1.5 rounded-lg text-gray-600 hover:text-black hover:bg-white/60 transition flex items-center gap-1.5"
                >
                  <span>🎡</span> Spin Wheel
                </Link>
              </div>

              <button
                onClick={() => {
                  if (activeTab === "settings") fetchSettings(true);
                  else fetchHistory(true);
                }}
                disabled={settingsLoading || historyLoading}
                className="flex items-center gap-1.5 text-xs font-bold text-gray-700 bg-white hover:bg-gray-50 border border-gray-200 px-3.5 py-2 rounded-xl transition shadow-2xs cursor-pointer"
              >
                <FiRefreshCw
                  size={13}
                  className={settingsLoading || historyLoading ? "animate-spin text-red-600" : ""}
                />
                <span>Refresh</span>
              </button>
            </div>
          </div>

          {/* 5 Core Items in Single Unified Row (Event Selector + 4 Key Metrics) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3.5">
            {/* 1. Event Selector Card */}
            <div className="bg-white rounded-2xl shadow-xs border border-amber-200/80 bg-gradient-to-br from-white to-amber-50/20 p-4 flex flex-col justify-between hover:border-amber-300 transition">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-amber-800 uppercase tracking-wider flex items-center gap-1.5">
                  <FiCalendar size={13} className="text-amber-600" />
                  <span>Select Event</span>
                </span>
                {selectedEventObj?.status && (
                  <span
                    className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase ${
                      selectedEventObj.status.toLowerCase() === "running"
                        ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
                        : "bg-gray-100 text-gray-600 border border-gray-200"
                    }`}
                  >
                    {selectedEventObj.status}
                  </span>
                )}
              </div>
              <div className="mt-1.5">
                <select
                  value={selectedEventId}
                  onChange={(e) => handleEventChange(e.target.value)}
                  className="w-full bg-white hover:bg-gray-50 border border-gray-300 text-gray-900 font-bold text-xs rounded-xl px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#ffff00] cursor-pointer shadow-2xs"
                >
                  {activeRunningEvent && (
                    <option value={activeRunningEvent.eventId || activeRunningEvent.id}>
                      🟢 {activeRunningEvent.eventName || `Event #${activeRunningEvent.id}`}
                    </option>
                  )}
                  <option value="all">🏆 All Events (Global)</option>
                  {eventsList
                    .filter(
                      (ev) =>
                        !activeRunningEvent ||
                        String(ev.eventId || ev.id) !==
                          String(activeRunningEvent.eventId || activeRunningEvent.id)
                    )
                    .map((ev) => {
                      const isCompleted = ev.status?.toLowerCase() === "completed";
                      return (
                        <option key={ev.eventId || ev.id} value={ev.eventId || ev.id}>
                          {isCompleted ? "⚪ " : "🟡 "}
                          {ev.eventName || `Event #${ev.id}`}
                        </option>
                      );
                    })}
                </select>
              </div>
              <p className="text-[11px] text-gray-400 mt-1.5 font-medium truncate">
                Filter metrics &amp; charts
              </p>
            </div>

            {/* 2. Today Total Session */}
            <div className="bg-white rounded-2xl shadow-xs border border-gray-200/80 p-4 flex flex-col justify-between hover:border-gray-300 transition">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                  Today Total Sessions
                </span>
                <div className="w-6 h-6 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                  <FiClock size={13} />
                </div>
              </div>
              <p className="text-2xl font-black text-gray-900 font-mono mt-1.5">
                {(stats?.todaySessions ?? 0).toLocaleString()}
              </p>
              <p className="text-[11px] text-gray-400 mt-1.5 font-medium">
                Sessions played today (UTC)
              </p>
            </div>

            {/* 3. Today's Awarded Points */}
            <div className="bg-white rounded-2xl shadow-xs border border-emerald-200/80 bg-gradient-to-br from-white to-emerald-50/20 p-4 flex flex-col justify-between hover:border-emerald-300 transition">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-emerald-800 uppercase tracking-wider">
                  Today's Awarded Points
                </span>
                <div className="w-6 h-6 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold">
                  <FiZap size={13} />
                </div>
              </div>
              <p className="text-2xl font-black text-emerald-700 font-mono mt-1.5">
                +{ (stats?.todayPoints ?? 0).toLocaleString() }{" "}
                <span className="text-[11px] font-sans text-emerald-600 font-bold">PTS</span>
              </p>
              <p className="text-[11px] text-emerald-700/80 mt-1.5 font-medium">
                Claimed by players today
              </p>
            </div>

            {/* 4. Event's Total Points */}
            <div className="bg-white rounded-2xl shadow-xs border border-purple-200/80 bg-gradient-to-br from-white to-purple-50/20 p-4 flex flex-col justify-between hover:border-purple-300 transition">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-purple-800 uppercase tracking-wider">
                  Event's Total Points
                </span>
                <div className="w-6 h-6 rounded-lg bg-purple-100 text-purple-700 flex items-center justify-center font-bold">
                  <FiAward size={13} />
                </div>
              </div>
              <p className="text-2xl font-black text-purple-700 font-mono mt-1.5">
                {(stats?.eventTotalPoints ?? 0).toLocaleString()}{" "}
                <span className="text-[11px] font-sans text-purple-600 font-bold">PTS</span>
              </p>
              <p className="text-[11px] text-purple-700/80 mt-1.5 font-medium truncate">
                Cumulative in {selectedEventName}
              </p>
            </div>

            {/* 5. Event's Total Sessions */}
            <div className="bg-white rounded-2xl shadow-xs border border-amber-200/80 bg-gradient-to-br from-white to-amber-50/20 p-4 flex flex-col justify-between hover:border-amber-300 transition">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-amber-900 uppercase tracking-wider">
                  Event's Total Sessions
                </span>
                <div className="w-6 h-6 rounded-lg bg-amber-100 text-amber-800 flex items-center justify-center font-bold">
                  <FiLayers size={13} />
                </div>
              </div>
              <p className="text-2xl font-black text-amber-900 font-mono mt-1.5">
                {(stats?.eventTotalSessions ?? 0).toLocaleString()}
              </p>
              <p className="text-[11px] text-amber-800/80 mt-1.5 font-medium truncate">
                All rounds in {selectedEventName}
              </p>
            </div>
          </div>

          {/* 2 CHARTS (Update on date change, default to active event) */}
          <GameAnalyticsCharts
            history={analyticsHistory.length > 0 ? analyticsHistory : history}
            dailyStats={dailyStats}
            stats={stats}
            gameName="777 Slots"
            themeColor="red"
          />

          {/* SUB-NAVIGATION TABS (Settings & Rewards, Spin History & Logs) */}
          <div className="flex gap-2 bg-white border border-gray-200 rounded-xl p-1.5 w-fit shadow-xs">
            <button
              type="button"
              onClick={() => setActiveTab("settings")}
              className={`px-5 py-2 text-xs font-bold rounded-lg transition flex items-center gap-2 cursor-pointer ${
                activeTab === "settings"
                  ? "bg-[#ffff00] text-black shadow-2xs"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
              }`}
            >
              <FiSettings size={14} />
              <span>Game Settings &amp; Rewards</span>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab("history")}
              className={`px-5 py-2 text-xs font-bold rounded-lg transition flex items-center gap-2 cursor-pointer ${
                activeTab === "history"
                  ? "bg-[#ffff00] text-black shadow-2xs"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
              }`}
            >
              <FiList size={14} />
              <span>Spin History &amp; Logs</span>
            </button>
          </div>

          {/* TAB 1: GAME SETTINGS */}
          {activeTab === "settings" && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 sm:p-7">
              {settingsLoading ? (
                <div className="py-16 flex flex-col items-center justify-center gap-3 text-gray-400">
                  <div className="w-8 h-8 border-4 border-[#ffff00] border-t-transparent rounded-full animate-spin" />
                  <p className="text-xs font-semibold text-gray-500">Loading 777 Slots settings...</p>
                </div>
              ) : (
                <form onSubmit={handleSaveSettings} className="space-y-6">
                  {/* Status Toggle */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
                    <div>
                      <label className="text-sm font-bold text-gray-800">
                        777 Slot Machine Status
                      </label>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Enable or disable the 777 Slots game in the mobile app.
                      </p>
                    </div>

                    <div className="flex items-center gap-3">
                      <span className={`text-xs font-bold ${formSettings.isActive ? "text-emerald-700" : "text-gray-500"}`}>
                        {formSettings.isActive ? "Active (Enabled)" : "Disabled"}
                      </span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formSettings.isActive}
                          onChange={(e) =>
                            setFormSettings((prev) => ({ ...prev, isActive: e.target.checked }))
                          }
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#ffff00]"></div>
                      </label>
                    </div>
                  </div>

                  {/* Point Combination Rules */}
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <span>🎰</span> Point Combination Payouts
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                      {/* Jackpot 777 */}
                      <div className="bg-red-50/50 border border-red-200 rounded-xl p-4">
                        <label className="block text-xs font-black text-red-900 mb-1">
                          7️⃣ 7️⃣ 7️⃣ Jackpot Points
                        </label>
                        <input
                          type="number"
                          min={1}
                          max={50000}
                          value={formSettings.jackpotPoints}
                          onChange={(e) =>
                            setFormSettings((prev) => ({ ...prev, jackpotPoints: e.target.value }))
                          }
                          className="w-full px-3.5 py-2.5 bg-white border border-red-300 rounded-xl text-sm font-bold text-gray-900 focus:outline-none focus:ring-2 focus:ring-red-400"
                        />
                        <span className="text-[11px] text-red-700 mt-1 block font-medium">
                          Payout when 3 Sevens align (Default: 500)
                        </span>
                      </div>

                      {/* Triple Bar */}
                      <div className="bg-amber-50/50 border border-amber-200 rounded-xl p-4">
                        <label className="block text-xs font-black text-amber-900 mb-1">
                          🍫 🍫 🍫 Triple Bar Points
                        </label>
                        <input
                          type="number"
                          min={1}
                          max={10000}
                          value={formSettings.tripleBarPoints}
                          onChange={(e) =>
                            setFormSettings((prev) => ({ ...prev, tripleBarPoints: e.target.value }))
                          }
                          className="w-full px-3.5 py-2.5 bg-white border border-amber-300 rounded-xl text-sm font-bold text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-400"
                        />
                        <span className="text-[11px] text-amber-700 mt-1 block font-medium">
                          Payout for 3 Bar symbols (Default: 200)
                        </span>
                      </div>

                      {/* Triple Cherry */}
                      <div className="bg-purple-50/50 border border-purple-200 rounded-xl p-4">
                        <label className="block text-xs font-black text-purple-900 mb-1">
                          🍒 🍒 🍒 Triple Cherry Points
                        </label>
                        <input
                          type="number"
                          min={1}
                          max={5000}
                          value={formSettings.tripleCherryPoints}
                          onChange={(e) =>
                            setFormSettings((prev) => ({ ...prev, tripleCherryPoints: e.target.value }))
                          }
                          className="w-full px-3.5 py-2.5 bg-white border border-purple-300 rounded-xl text-sm font-bold text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-400"
                        />
                        <span className="text-[11px] text-purple-700 mt-1 block font-medium">
                          Payout for 3 Cherry symbols (Default: 100)
                        </span>
                      </div>

                      {/* Other Triple Matches */}
                      <div className="bg-emerald-50/50 border border-emerald-200 rounded-xl p-4">
                        <label className="block text-xs font-black text-emerald-900 mb-1">
                          🍋 🍉 Other Triple Match Points
                        </label>
                        <input
                          type="number"
                          min={1}
                          max={2000}
                          value={formSettings.otherTriplePoints}
                          onChange={(e) =>
                            setFormSettings((prev) => ({ ...prev, otherTriplePoints: e.target.value }))
                          }
                          className="w-full px-3.5 py-2.5 bg-white border border-emerald-300 rounded-xl text-sm font-bold text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                        />
                        <span className="text-[11px] text-emerald-700 mt-1 block font-medium">
                          Payout for 3 Lemons or Watermelons (Default: 50)
                        </span>
                      </div>

                      {/* Any 2 Matching Symbols */}
                      <div className="bg-blue-50/50 border border-blue-200 rounded-xl p-4">
                        <label className="block text-xs font-black text-blue-900 mb-1">
                          🍒 🍒 Any 2 Match Points
                        </label>
                        <input
                          type="number"
                          min={1}
                          max={1000}
                          value={formSettings.match2Points}
                          onChange={(e) =>
                            setFormSettings((prev) => ({ ...prev, match2Points: e.target.value }))
                          }
                          className="w-full px-3.5 py-2.5 bg-white border border-blue-300 rounded-xl text-sm font-bold text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-400"
                        />
                        <span className="text-[11px] text-blue-700 mt-1 block font-medium">
                          Payout when any 2 symbols match (Default: 20)
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Probability Distribution */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                        <span>📊</span> Spin Outcome Probability Distribution
                      </h3>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={handleSlotsAutoBalance}
                          className="text-xs text-blue-600 hover:text-blue-800 font-semibold cursor-pointer underline"
                        >
                          Auto-Balance Loss %
                        </button>
                        <span className="text-gray-300">|</span>
                        <button
                          type="button"
                          onClick={handleSlotsResetProbabilities}
                          className="text-xs text-red-600 hover:text-red-800 font-semibold cursor-pointer underline"
                        >
                          Reset Defaults
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                      {/* Jackpot Chance */}
                      <div className="bg-white border border-red-200 border-l-4 border-l-red-500 rounded-xl p-3 shadow-2xs">
                        <label className="block text-[11px] font-black text-red-900 mb-1">
                          7️⃣ 7️⃣ 7️⃣ Jackpot (%)
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            min={0}
                            max={100}
                            value={pointProbabilities.jackpotPercentage}
                            onChange={(e) => handleSlotsProbChange("jackpotPercentage", e.target.value)}
                            className="w-full px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs font-black text-gray-900 focus:outline-none focus:ring-1 focus:ring-red-400 pr-7"
                          />
                          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400 font-bold">%</span>
                        </div>
                        <span className="text-[10px] text-gray-400 mt-1 block">Default: 2% chance</span>
                      </div>

                      {/* Triple Bar Chance */}
                      <div className="bg-white border border-amber-200 border-l-4 border-l-amber-500 rounded-xl p-3 shadow-2xs">
                        <label className="block text-[11px] font-black text-amber-900 mb-1">
                          🍫 🍫 🍫 Triple Bar (%)
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            min={0}
                            max={100}
                            value={pointProbabilities.tripleBarPercentage}
                            onChange={(e) => handleSlotsProbChange("tripleBarPercentage", e.target.value)}
                            className="w-full px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs font-black text-gray-900 focus:outline-none focus:ring-1 focus:ring-amber-400 pr-7"
                          />
                          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400 font-bold">%</span>
                        </div>
                        <span className="text-[10px] text-gray-400 mt-1 block">Default: 5% chance</span>
                      </div>

                      {/* Triple Cherry Chance */}
                      <div className="bg-white border border-purple-200 border-l-4 border-l-purple-500 rounded-xl p-3 shadow-2xs">
                        <label className="block text-[11px] font-black text-purple-900 mb-1">
                          🍒 🍒 🍒 Cherry (%)
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            min={0}
                            max={100}
                            value={pointProbabilities.tripleCherryPercentage}
                            onChange={(e) => handleSlotsProbChange("tripleCherryPercentage", e.target.value)}
                            className="w-full px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs font-black text-gray-900 focus:outline-none focus:ring-1 focus:ring-purple-400 pr-7"
                          />
                          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400 font-bold">%</span>
                        </div>
                        <span className="text-[10px] text-gray-400 mt-1 block">Default: 10% chance</span>
                      </div>

                      {/* Other Triple Chance */}
                      <div className="bg-white border border-emerald-200 border-l-4 border-l-emerald-500 rounded-xl p-3 shadow-2xs">
                        <label className="block text-[11px] font-black text-emerald-900 mb-1">
                          🍋 🍉 Match 3 (%)
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            min={0}
                            max={100}
                            value={pointProbabilities.otherTriplePercentage}
                            onChange={(e) => handleSlotsProbChange("otherTriplePercentage", e.target.value)}
                            className="w-full px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs font-black text-gray-900 focus:outline-none focus:ring-1 focus:ring-emerald-400 pr-7"
                          />
                          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400 font-bold">%</span>
                        </div>
                        <span className="text-[10px] text-gray-400 mt-1 block">Default: 15% chance</span>
                      </div>

                      {/* Match 2 Chance */}
                      <div className="bg-white border border-blue-200 border-l-4 border-l-blue-500 rounded-xl p-3 shadow-2xs">
                        <label className="block text-[11px] font-black text-blue-900 mb-1">
                          🍒 🍒 Any 2 (%)
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            min={0}
                            max={100}
                            value={pointProbabilities.match2Percentage}
                            onChange={(e) => handleSlotsProbChange("match2Percentage", e.target.value)}
                            className="w-full px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs font-black text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-400 pr-7"
                          />
                          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400 font-bold">%</span>
                        </div>
                        <span className="text-[10px] text-gray-400 mt-1 block">Default: 35% chance</span>
                      </div>

                      {/* Loss / No Match Chance */}
                      <div className="bg-white border border-gray-300 border-l-4 border-l-gray-400 rounded-xl p-3 shadow-2xs">
                        <label className="block text-[11px] font-black text-gray-800 mb-1">
                          ❌ No Match (%)
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            min={0}
                            max={100}
                            value={pointProbabilities.lossPercentage}
                            onChange={(e) => handleSlotsProbChange("lossPercentage", e.target.value)}
                            className="w-full px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs font-black text-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-400 pr-7"
                          />
                          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400 font-bold">%</span>
                        </div>
                        <span className="text-[10px] text-gray-400 mt-1 block">Default: 33% chance</span>
                      </div>
                    </div>
                  </div>

                  {/* Timing and Spin Limits */}
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <span>⏱️</span> Session Rules &amp; Cooldown
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1">
                          Total Spins Per Round
                        </label>
                        <input
                          type="number"
                          min={1}
                          max={20}
                          value={formSettings.totalSpins}
                          onChange={(e) =>
                            setFormSettings((prev) => ({ ...prev, totalSpins: e.target.value }))
                          }
                          className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#ffff00] focus:bg-white transition"
                        />
                        <span className="text-[11px] text-gray-400 mt-1 block">
                          Default: 5 spins per session
                        </span>
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1">
                          Break / Cooldown Time (Seconds)
                        </label>
                        <input
                          type="number"
                          min={0}
                          max={86400}
                          value={formSettings.cooldownSeconds}
                          onChange={(e) =>
                            setFormSettings((prev) => ({ ...prev, cooldownSeconds: e.target.value }))
                          }
                          className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#ffff00] focus:bg-white transition"
                        />
                        <span className="text-[11px] text-gray-400 mt-1 block">
                          180s = 3 minutes wait time between sessions
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className="pt-4 border-t border-gray-100 flex items-center justify-end">
                    <button
                      type="submit"
                      disabled={savingSettings}
                      className="px-6 py-2.5 bg-[#ffff00] hover:bg-[#e6e600] text-black font-bold text-xs rounded-xl shadow-xs transition flex items-center gap-2 cursor-pointer disabled:opacity-50"
                    >
                      {savingSettings ? (
                        <>
                          <FiRefreshCw className="h-3.5 w-3.5 animate-spin" />
                          <span>Saving Settings...</span>
                        </>
                      ) : (
                        <>
                          <FiSave className="h-3.5 w-3.5" />
                          <span>Save 777 Slots Settings</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}

          {/* TAB 2: AUDIT HISTORY */}
          {activeTab === "history" && (
            <div className="space-y-4">
              {/* Filter Toolbar */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="relative flex-1 max-w-md">
                  <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 h-3.5 w-3.5" />
                  <input
                    type="text"
                    placeholder="Search username, session ID, email..."
                    value={search}
                    onChange={handleSearchChange}
                    className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-900 placeholder-gray-400 focus:outline-none focus:border-yellow-400 focus:bg-white"
                  />
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  {/* Status Filter */}
                  <select
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setPage(1);
                    }}
                    className="bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-700 px-3 py-2 focus:outline-none focus:border-yellow-400 cursor-pointer"
                  >
                    <option value="all">All Status</option>
                    <option value="completed">Completed</option>
                    <option value="started">Started</option>
                    <option value="expired">Expired</option>
                  </select>
                </div>
              </div>

              {/* History Table */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                {historyLoading ? (
                  <div className="p-12 text-center">
                    <div className="w-8 h-8 border-3 border-[#ffff00] border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                    <p className="text-gray-500 text-xs font-semibold">Loading 777 Slots history...</p>
                  </div>
                ) : history.length === 0 ? (
                  <div className="p-12 text-center">
                    <FiInfo className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-800 font-bold text-sm">No 777 Slots History Found</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {selectedEventId && selectedEventId !== "all" ? `No spin records recorded for ${selectedEventName}.` : "No records found matching filters."}
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse text-xs">
                      <thead>
                        <tr className="border-b border-gray-200 bg-gray-50 text-gray-500 font-bold uppercase tracking-wider text-[11px]">
                          <th className="py-3.5 px-4">Session ID</th>
                          <th className="py-3.5 px-4">User</th>
                          <th className="py-3.5 px-4">Event</th>
                          <th className="py-3.5 px-4 text-center">Spins</th>
                          <th className="py-3.5 px-4 text-right">Awarded Points</th>
                          <th className="py-3.5 px-4 text-center">Status</th>
                          <th className="py-3.5 px-4">Timestamp</th>
                          <th className="py-3.5 px-4 text-center">View</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {history.map((item, idx) => {
                          const sid = item.sessionId || item.session_id || item.id;
                          const shortSid = sid ? `${String(sid).substring(0, 12)}...` : "—";
                          const isCompleted = item.status === "completed" || item.status === "Claimed";
                          const claimedPts = Number(item.claimedPoints ?? item.pointsAwarded ?? 0);
                          const userId = item.userId || item.user_id || item.user?.id;
                          const userName = item.username || item.userName || item.user?.username || `User #${userId || "—"}`;
                          const spinsCount = Array.isArray(item.spinsData) ? item.spinsData.length : (item.totalSpins || 5);
                          const eventName = item.eventName || (item.eventId ? `Event #${item.eventId}` : "General");

                          return (
                            <tr
                              key={sid || idx}
                              onClick={() => setSelectedSessionModal(item)}
                              className="hover:bg-amber-50/30 transition duration-150 cursor-pointer group"
                              title="Click to view 5 chances points breakdown"
                            >
                              <td className="py-3.5 px-4 whitespace-nowrap font-mono text-gray-700">
                                <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                                  <span>{shortSid}</span>
                                  {sid && (
                                    <button
                                      onClick={() => handleCopySession(sid)}
                                      className="text-gray-400 hover:text-black p-1 rounded transition cursor-pointer"
                                      title="Copy ID"
                                    >
                                      {copiedSessionId === sid ? (
                                        <FiCheck className="h-3.5 w-3.5 text-emerald-600" />
                                      ) : (
                                        <FiCopy className="h-3.5 w-3.5" />
                                      )}
                                    </button>
                                  )}
                                </div>
                              </td>

                              <td className="py-3.5 px-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                {userId ? (
                                  <Link
                                    to={`/user/${userId}`}
                                    className="font-bold text-gray-900 hover:text-red-700 transition"
                                  >
                                    {userName}
                                  </Link>
                                ) : (
                                  <span className="text-gray-800 font-semibold">{userName}</span>
                                )}
                              </td>

                              {/* Event Column */}
                              <td className="py-3.5 px-4 whitespace-nowrap">
                                <span className="inline-flex items-center gap-1 font-bold text-[11px] px-2.5 py-0.5 rounded-lg bg-amber-50 text-amber-900 border border-amber-200">
                                  <span>🏆</span>
                                  <span className="truncate max-w-[130px]" title={eventName}>
                                    {eventName}
                                  </span>
                                </span>
                              </td>

                              <td className="py-3.5 px-4 whitespace-nowrap text-center">
                                <span className="px-2 py-0.5 rounded-md bg-gray-100 font-mono font-bold text-gray-700">
                                  {spinsCount} spins
                                </span>
                              </td>

                              {/* Awarded Points */}
                              <td className="py-3.5 px-4 whitespace-nowrap text-right font-mono font-bold text-purple-700">
                                +{claimedPts.toLocaleString()} pts
                              </td>

                              <td className="py-3.5 px-4 whitespace-nowrap text-center">
                                <span
                                  className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                                    isCompleted
                                      ? "bg-emerald-50 text-emerald-700 border border-emerald-300"
                                      : item.status === "started"
                                      ? "bg-blue-50 text-blue-700 border border-blue-300"
                                      : "bg-gray-100 text-gray-600 border border-gray-200"
                                  }`}
                                >
                                  {item.status || "Unknown"}
                                </span>
                              </td>

                              <td className="py-3.5 px-4 whitespace-nowrap text-gray-500 text-[11px]">
                                {formatDateTime(item.createdAt || item.startedAt || item.timestamp)}
                              </td>

                              <td className="py-3.5 px-4 whitespace-nowrap text-center">
                                <button
                                  onClick={() => setSelectedSessionModal(item)}
                                  className="p-1.5 rounded-lg bg-gray-100 hover:bg-[#ffff00] text-gray-700 hover:text-black transition cursor-pointer"
                                  title="View Details"
                                >
                                  <FiEye className="h-3.5 w-3.5" />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Pagination */}
                <div className="p-3.5 bg-gray-50 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-600">
                  <span>Total: {pagination.total} logs</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page <= 1 || historyLoading}
                      className="px-3 py-1 bg-white border border-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed shadow-2xs cursor-pointer"
                    >
                      Prev
                    </button>
                    <span className="font-bold text-gray-800">
                      Page {page} of {pagination.totalPages}
                    </span>
                    <button
                      onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                      disabled={page >= pagination.totalPages || historyLoading}
                      className="px-3 py-1 bg-white border border-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed shadow-2xs cursor-pointer"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Session Details Modal */}
      {selectedSessionModal && (
        <GameSessionDetailModal
          session={selectedSessionModal}
          onClose={() => setSelectedSessionModal(null)}
          gameType="slots"
        />
      )}
    </div>
  );
}
